import {
  DataConsumer,
  DataConsumerBase,
  DataConsumerContactMutableProperties,
  DataConsumerMutableProperties,
  PagedResponse,
} from "./models";
import {
  ApiCall,
  ApiErrorResponse,
  ApiState,
  PagedApiState,
  UpdateResult,
  useApiState,
  useAuthorizedFetch,
  useGetAuthorized,
  usePagedApiState,
} from "./useApiState";

const baseUrl = "int-platform/consumers";

export function useDataConsumer(
  consumerId: string
): ApiState<DataConsumer | undefined> {
  return useApiState(
    useGetAuthorized<DataConsumer>(
      `${baseUrl}/${consumerId}`,
      undefined,
      undefined,
      undefined,
      "integration-platform"
    ),
    undefined
  );
}

export function useDataConsumers(
  organizationId?: string,
  publicConsumer: boolean = true
): PagedApiState<DataConsumer> {
  let modifiedUrl = baseUrl;

  if (organizationId) {
    modifiedUrl += `?org_id=${organizationId}&public=${publicConsumer}`;
  } else {
    modifiedUrl += `?public=${publicConsumer}`;
  }

  return usePagedApiState(
    useGetAuthorized<PagedResponse<DataConsumer>>(
      modifiedUrl,
      undefined,
      undefined,
      undefined,
      "integration-platform"
    ),
    undefined
  );
}

export function useDataConsumersAll(
  organizationId?: string
): PagedApiState<DataConsumer> {
  const dataConsumersPublic = useDataConsumers(organizationId);
  const dataConsumersPrivate = useDataConsumers(organizationId, false);

  return {
    loading: dataConsumersPublic.loading || dataConsumersPrivate.loading,
    failed: dataConsumersPublic.failed || dataConsumersPrivate.failed,
    refresh: async () => {
      await Promise.all([
        dataConsumersPublic.refresh(),
        dataConsumersPrivate.refresh(),
      ]);
    },
    value: {
      items: [
        ...(dataConsumersPublic.value?.items || []),
        ...(dataConsumersPrivate.value?.items || []),
      ],
      totalCount:
        (dataConsumersPublic.value?.items?.length || 0) +
        (dataConsumersPrivate.value?.items?.length || 0),
    },
    currentPage: dataConsumersPublic.currentPage,
    currentPageSize: dataConsumersPublic.currentPageSize,
    goToPage: dataConsumersPublic.goToPage,
    setPageSize: dataConsumersPublic.setPageSize,
  };
}

export function useCreateDataConsumer(): ApiCall<
  { name: string; orgId: string; public: boolean },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (dataConsumer: {
    name: string;
    orgId: string;
    public: boolean;
  }) => {
    const result = await authorizedFetch(baseUrl, {
      method: "post",
      body: JSON.stringify(dataConsumer),
    });
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          data: await result.json().then((data) => data as DataConsumer),
          detail: { message: "Data consumer created successfully" },
        },
      };
    }
  };
}

export function useDeleteDataConsumer(): ApiCall<
  { dataConsumerIds: string[] },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (input: {
    dataConsumerIds: string[];
  }): Promise<UpdateResult> => {
    try {
      await Promise.all(
        input.dataConsumerIds.map(async (id) => {
          const result = await authorizedFetch(`${baseUrl}/${id}`, {
            method: "delete",
          });

          if (!result.ok) {
            throw new Error(`Failed to delete data consumer with id: ${id}`);
          }
        })
      );
      return {
        error: false,
        response: {
          statusCode: 200,
          detail: { message: "All data consumers deleted successfully" },
        },
      };
    } catch (error) {
      return {
        error: true,
        response: {
          statusCode: 500,
          detail: {
            message: error instanceof Error ? error.message : "Unknown error",
          },
        },
      };
    }
  };
}

export function useUpdateDataConsumer(
  dataConsumerId: string
): ApiCall<DataConsumerMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (dataConsumer: DataConsumerMutableProperties) => {
    const result = await authorizedFetch(`${baseUrl}/${dataConsumerId}`, {
      method: "put",
      body: JSON.stringify(dataConsumer),
    });
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          data: await result.json().then((data) => data as DataConsumer),
          detail: { message: "Data consumer updated" },
        },
      };
    }
  };
}

export function useUpdateDataConsumerContact(
  dataConsumerId: string
): ApiCall<DataConsumerContactMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (dataConsumer: DataConsumerContactMutableProperties) => {
    const result = await authorizedFetch(
      `${baseUrl}/${dataConsumerId}/contacts`,
      {
        method: "POST",
        body: JSON.stringify(dataConsumer),
      }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Data consumer contact updated" },
        },
      };
    }
  };
}

export function useDataConsumersPublic(): PagedApiState<DataConsumerBase> {
  const url = "consumers/public";

  const get = useGetAuthorized<PagedResponse<DataConsumerBase>>(url);
  return usePagedApiState(get);
}

export function useOrganizationDataConsumers(
  organizationId: string
): PagedApiState<DataConsumer> {
  const url = `organizations/${organizationId}/consumers`;

  const get = useGetAuthorized<PagedResponse<DataConsumer>>(url);
  return usePagedApiState(get);
}

export function useAddDataConsumersToOrganization(
  organizationId: string
): ApiCall<{ consumerIds: string[] }, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true);
  return async (input: { consumerIds: string[] }) => {
    const result = await authorizedFetch(
      `organizations/${organizationId}/consumers`,
      {
        method: "PUT",
        body: JSON.stringify(input.consumerIds),
      }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Data consumers added to organization" },
        },
      };
    }
  };
}

export function useRemoveDataConsumersFromOrganization(
  organizationId: string
): ApiCall<{ consumerIds: string[] }, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true);
  return async (input: { consumerIds: string[] }) => {
    const result = await authorizedFetch(
      `organizations/${organizationId}/consumers`,
      {
        method: "DELETE",
        body: JSON.stringify(input.consumerIds),
      }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Data consumers removed to organization" },
        },
      };
    }
  };
}
